import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Clock, GripVertical, Users } from 'lucide-react';
import { Card, Badge, TimeContainer, Tooltip } from './styles';
import { PendenciaKanban, STATUS_CONCLUIDAS } from './types';
import { comum } from '../../../styles/Themes/light';
import { formatTipo, getTipoIcon, formatarTempoRelativoPendencia, podeMoverPendencia } from './utils';

interface CardPendenciaProps {
    pendencia: PendenciaKanban;
    onView?: (pendencia: PendenciaKanban) => void;
    userProfile?: string;
    userId?: number | null;
    idEscritorioSolicitante?: number | null;
}

const CardPendencia: React.FC<CardPendenciaProps> = ({ pendencia, onView, userProfile, userId, idEscritorioSolicitante }) => {
    // Verifica se o usuário pode mover esta pendência
    const podeMovimentar = useMemo(() => 
        podeMoverPendencia(pendencia, userProfile, userId, idEscritorioSolicitante),
        [pendencia, userProfile, userId, idEscritorioSolicitante]
    );

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ 
        id: pendencia.id_pendencia.toString(),
        disabled: !podeMovimentar // Desabilita o drag se não tiver permissão
    });

    const style = useMemo(() => ({
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 200ms ease',
    }), [transform, transition]);

    const TipoIcon = useMemo(() => getTipoIcon(pendencia.tipo), [pendencia.tipo]);

    const badgeContent = useMemo(() => {
        const isConcluida = pendencia.status_kanban === STATUS_CONCLUIDAS;
        
        if (isConcluida && pendencia.resultado) {
            const variant = pendencia.resultado === 'Resolvida' ? 'resolvida' :
                           pendencia.resultado === 'Aceita' ? 'aceita' : 'recusada';
            return (
                <>
                    <Badge variant="tipo">
                        {formatTipo(pendencia.tipo)}
                    </Badge>
                    <Badge variant={variant}>
                        {pendencia.resultado}
                    </Badge>
                </>
            );
        }
        
        return (
            <Badge variant="tipo">
                {formatTipo(pendencia.tipo)}
            </Badge>
        );
    }, [pendencia.status_kanban, pendencia.resultado, pendencia.tipo]);

    return (
        <Card
            ref={setNodeRef}
            style={style}
            isDragging={isDragging}
            canMove={podeMovimentar}
            className={`card ${isDragging ? 'dragging' : ''}`}
            {...attributes}
            {...(podeMovimentar ? listeners : {})} // Só aplica listeners se puder mover
            onTouchStart={(e) => {
                // Permite que o touch inicie o drag apenas se tiver permissão
                if (podeMovimentar) {
                    e.stopPropagation();
                }
            }}
            onClick={(e) => {
                // Só abre o modal se não estiver arrastando
                if (!isDragging && onView) {
                    e.stopPropagation();
                    onView(pendencia);
                }
            }}
        >
            <div className="card-header">
                <div className="badges">
                    {badgeContent}
                </div>
                <div className="header-right">
                    <span className="pendencia-id">
                        #{pendencia.id_pendencia}
                    </span>
                    <div className="icons">
                        <TipoIcon size={14} color={comum.colors.interfaceText400} />
                        <GripVertical 
                            size={14} 
                            color={comum.colors.interfaceText400} 
                            style={{ opacity: 0, transition: 'opacity 0.2s' }}
                            className="grip-icon"
                        />
                    </div>
                </div>
            </div>
            
            <div className="card-content">
                <p>{pendencia.descricao}</p>
            </div>
            
            <div className="card-footer">
                <div className="footer-left">
                    <Users size={14} color={comum.colors.interfaceText300} style={{ flexShrink: 0 }} />
                    <span className="criador-nome">{pendencia.criadorNome || 'Sistema'}</span>
                    {pendencia.origem && (
                        <Badge variant="tipo" style={{ fontSize: '10px', fontWeight: 400, padding: '2px 6px', height: '20px', flexShrink: 0 }}>
                            {pendencia.origem === 'Correspondente' ? 'Profissional' : pendencia.origem}
                        </Badge>
                    )}
                </div>
                <TimeContainer>
                    <div className="time">
                        <Clock size={10} color={comum.colors.interfaceText300} />
                        <span>{formatarTempoRelativoPendencia(pendencia.criado)}</span>
                    </div>
                    <Tooltip>
                        {new Date(pendencia.criado).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }).replace(',', ' às')}
                    </Tooltip>
                </TimeContainer>
            </div>
        </Card>
    );
};

export default CardPendencia;

